import { MagistralDrugTemplate, MagistralDrugTemplateQuery } from '@dreryk/drerykweb-medicine-api';

import { PromiseVoidFunction } from '@dreryk/react-common';
import { TextPattern } from '@dreryk/watermelon';

import { FormsTemplates } from '~libs.desktop.shared.user-templates.forms';

import { magistralDrugTemplatesTranslationKeys } from '../../../../translations';

import {
   MagistralDrugTemplateForm,
   MagistralDrugTemplateFormChildrenRenderProps,
} from './form/MagistralDrugTemplateForm';
import { MagistralDrugInitialEditTemplate, MagistralDrugTemplateListItem } from './MagistralDrugTemplates.types';
import { MagistralDrugTemplatesList } from './MagistralDrugTemplatesList';
import { MagistralDrugTemplatesSendTemplateButton } from './MagistralDrugTemplatesSendTemplateButton';
import { MagistralDrugTemplateInsert } from './readOnly/MagistralDrugReadOnly.types';
import { MagistralDrugTemplateReadOnly } from './readOnly/MagistralDrugTemplateReadOnly';

type MagistralDrugTemplatesEditSlotRenderProps = MagistralDrugTemplateFormChildrenRenderProps & {
   onTemplateSaveSuccess: PromiseVoidFunction;
   ready: boolean;
};

type MagistralDrugTemplatesProps = {
   readonly initialEditTemplate?: MagistralDrugInitialEditTemplate;
   readonly onInsert?: MagistralDrugTemplateInsert;
   readonly readOnlySlot?: JSX.Element;
   readonly editSlot: (renderProps: MagistralDrugTemplatesEditSlotRenderProps) => JSX.Element;
   readonly children?: JSX.Element;
};

const magistralDrugTemplatesSearchCreator = (searchText?: string | null): MagistralDrugTemplateQuery => {
   const query = new MagistralDrugTemplateQuery();
   if (searchText) {
      query.drugOrRawMaterialName = TextPattern.anywhereIgnoreCase(searchText);
   }
   return query;
};

// eslint-disable-next-line max-lines-per-function
export const MagistralDrugTemplates = (props: MagistralDrugTemplatesProps): JSX.Element => {
   return (
      <FormsTemplates>
         <FormsTemplates.Search<MagistralDrugTemplate>
            queryCreator={magistralDrugTemplatesSearchCreator}
            testId={'magistralDrugTemplatesSearch'}
            placeholder={magistralDrugTemplatesTranslationKeys.searchTemplate}
         >
            {/* eslint-disable-next-line max-lines-per-function */}
            {({ templates, templatesExist, loading, refetch }) => (
               <FormsTemplates.State<MagistralDrugTemplateListItem>
                  templates={templates}
                  initialEditItem={props.initialEditTemplate}
               >
                  <>
                     {loading ? null : <MagistralDrugTemplatesList onTemplateRemoved={refetch} />}

                     <FormsTemplates.ReadOnlySection<MagistralDrugTemplate>>
                        {({ activeItem }) => (
                           <>
                              <MagistralDrugTemplateReadOnly onInsert={props.onInsert} item={activeItem} />
                              {props.readOnlySlot}
                           </>
                        )}
                     </FormsTemplates.ReadOnlySection>
                     <FormsTemplates.FormSection<MagistralDrugTemplateListItem>>
                        {({ activeItem, disableSwitching, enableSwitching, setActiveItemName, templatesApiRef }) => (
                           <MagistralDrugTemplateForm
                              template={activeItem}
                              onDirty={disableSwitching}
                              onPristine={enableSwitching}
                              templatesApiRef={templatesApiRef}
                              onTemplateNameChange={setActiveItemName}
                           >
                              {(formSectionProps) =>
                                 props.editSlot({ ...formSectionProps, ready: loading, onTemplateSaveSuccess: refetch })
                              }
                           </MagistralDrugTemplateForm>
                        )}
                     </FormsTemplates.FormSection>

                     {props.children}

                     {templatesExist || loading ? null : (
                        <>
                           <FormsTemplates.EmptyStatePlaceholder
                              subheading={magistralDrugTemplatesTranslationKeys.noTemplatesAddedYet}
                           />
                           <FormsTemplates.AddNewItemButton />
                        </>
                     )}
                  </>
               </FormsTemplates.State>
            )}
         </FormsTemplates.Search>
      </FormsTemplates>
   );
};

MagistralDrugTemplates.SendTemplateButton = MagistralDrugTemplatesSendTemplateButton;
